"use client";

import React, { useState } from "react";
import { MapPin, Globe, ArrowRightLeft, Anchor } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
const logisticsNodes = [{
  id: "apex",
  title: "Apex, North Carolina",
  description: "Our strategic USA headquarters managing seamless distribution, client relations, and eco-conscious product lines.",
  icon: MapPin
}, {
  id: "hyderabad",
  title: "Hyderabad, India",
  description: "Our primary sourcing hub, connecting us directly to premium agricultural producers and authentic spice markets.",
  icon: Globe
}, {
  id: "bridge",
  title: "The Cosmic Bridge",
  description: "Facilitating high-quality global trade with reliable international logistics, bridging continents with trust.",
  icon: ArrowRightLeft
}];
export default function TheCosmicTradeBridge() {
  const [parallaxOffset, setParallaxOffset] = useState({
    x: 0,
    y: 0
  });
  const handleMouseMove = (e: React.MouseEvent<HTMLElement>) => {
    if (typeof window === "undefined") return;
    const {
      clientX,
      clientY
    } = e;
    const x = (clientX / window.innerWidth - 0.5) * 40;
    const y = (clientY / window.innerHeight - 0.5) * 40;
    setParallaxOffset({
      x,
      y
    });
  };
  return <section id="the-cosmic-trade-bridge" className="relative w-full overflow-hidden bg-background py-24 lg:py-32" onMouseMove={handleMouseMove}>
      {}
      <div className="pointer-events-none absolute inset-0 z-0 opacity-[0.03] mix-blend-multiply bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1578090/img/topographic_map_texture_light.png')] bg-cover bg-center" />

      {}
      <div className="pointer-events-none absolute inset-0 z-0 flex items-center justify-center overflow-hidden select-none">
        <span className="text-[40rem] font-black leading-none text-primary/[0.03] tracking-tighter">
          C
        </span>
      </div>

      <div className="container relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-12 lg:gap-8 items-center">
          
          {}
          <div className="lg:col-span-5 flex flex-col gap-6 relative z-20">
            <div className="mb-4">
              <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl mb-4">
                The Cosmic Trade Bridge
              </h2>
              <p className="text-lg text-muted-foreground">
                Connecting the rich agricultural heritage of India with the dynamic markets of the USA through transparent, premium logistics.
              </p>
            </div>

            <div className="flex flex-col gap-4 relative">
              {}
              <div className="absolute left-[27px] top-8 bottom-8 w-0.5 bg-gradient-to-b from-secondary/50 via-primary/20 to-secondary/50 hidden sm:block" />

              {logisticsNodes.map((node, i) => {
              const Icon = node.icon;
              return <Card key={node.id} data-index={i} className="relative overflow-hidden bg-card/60 backdrop-blur-md border-border/50 shadow-lg transition-transform hover:-translate-y-1 duration-300">
                    <CardHeader className="pb-2 flex flex-row items-center gap-4">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary ring-1 ring-primary/20">
                        <Icon className="h-5 w-5" />
                      </div>
                      <CardTitle className="text-xl text-foreground m-0">
                        {node.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground sm:pl-14">
                        {node.description}
                      </p>
                    </CardContent>
                  </Card>;
            })}
            </div>
          </div>

          {}
          <div className="lg:col-span-7 relative h-[500px] lg:h-[700px] w-full rounded-3xl border border-border/30 bg-primary/[0.02] overflow-hidden flex items-center justify-center">
            
            {}
            <div className="absolute inset-0 opacity-20 bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1578090/img/stylized_world_map_dots.png')] bg-contain bg-no-repeat bg-center" />

            {}
            <div className="absolute top-[60%] right-[25%] z-20 group">
              <span className="relative flex h-5 w-5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-secondary opacity-75"></span>
                <span className="relative inline-flex rounded-full h-5 w-5 bg-secondary border-2 border-background"></span>
              </span>
              <div className="absolute top-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-background/90 backdrop-blur-sm px-3 py-1.5 text-sm font-medium text-foreground shadow-sm border border-border/50 opacity-0 transition-opacity group-hover:opacity-100">
                Hyderabad Sourcing Hub
              </div>
            </div>

            {}
            <div className="absolute top-[40%] left-[20%] z-20 group">
              <span className="relative flex h-5 w-5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                <span className="relative inline-flex rounded-full h-5 w-5 bg-primary border-2 border-background"></span>
              </span>
              <div className="absolute top-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-background/90 backdrop-blur-sm px-3 py-1.5 text-sm font-medium text-foreground shadow-sm border border-border/50 opacity-0 transition-opacity group-hover:opacity-100">
                Apex HQ
              </div>
            </div>

            {}
            <svg className="absolute inset-0 h-full w-full pointer-events-none z-10" preserveAspectRatio="none">
              <path d="M 20% 40% Q 50% 20% 75% 60%" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="6 6" className="text-secondary/40 animate-[dash_20s_linear_infinite]" />
            </svg>

            {}
            <div className="relative z-30 transition-transform duration-200 ease-out will-change-transform" style={{
            transform: `translate(${parallaxOffset.x}px, ${parallaxOffset.y}px)`
          }}>
              <div className="relative">
                {}
                <div className="absolute -inset-4 rounded-full bg-secondary/20 blur-2xl" />
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1578090/img/cosmic_3d_cargo_ship_logistics.png" alt="Global Logistics Cargo Ship" className="relative h-48 w-auto object-contain drop-shadow-2xl sm:h-64 lg:h-80" />
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>;
}